/**
 * Labels, defaults and input config for the order quantity calculator.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Currency is the one locale-varying
 * piece and is resolved at render time from the request locale, not hardcoded
 * here.
 */

import type { DemandSpread } from "@/lib/bf-toolkit/calculations";

export const STRINGS = {
  toolTitle: "Your numbers",
  demand: "Expected demand (units)",
  confidence: "How sure are you about that?",
  price: "Selling price",
  cost: "Unit cost (COGS)",
  salvage: "Leftover units are worth…",
  orderStep: "Order in multiples of (optional)",
  orderStepPlaceholder: "e.g. 200",
  // Scenario column labels
  weaker: "If demand is weaker",
  expected: "If demand is as expected",
  stronger: "If demand is stronger",
  // Result copy
  orderPrefix: "Consider ordering",
  units: "units",
  demandRow: "Demand",
  soldRow: "Sold",
  leftOverRow: "Left over",
  shortRow: "Short",
  leftoverCostRow: "Lost on leftovers",
  leftoverRecoveredRow: "Recovered on leftovers",
  missedMarginRow: "Missed margin",
  profitRow: "Profit",
  sellsThrough: "Sells through exactly",
  disclaimer:
    "Estimates only, based on the numbers you enter. This models demand as a normal distribution around your estimate and balances the margin lost on a stock-out against the loss on leftover units. It doesn't account for fixed costs, reorders within the window, or the effect of price on demand.",
} as const;

/** Confidence chips → the demand spread the maths uses. */
export const CONFIDENCE_OPTIONS: { value: DemandSpread; label: string }[] = [
  { value: "tight", label: "Fairly sure" },
  { value: "wide", label: "Could be off a lot" },
];

/** What a leftover unit recovers, as a share of price/cost. Price-based, matching
 * the chip wording; "close to cost" recovers most of the unit cost (a small
 * liquidation haircut), never the full cost — so overstocking always carries at
 * least a token penalty and the trade-off stays visible. */
export type SalvageTier = "clearance" | "half" | "cost";

// Ordered most-valuable to least, matching how a founder thinks about what they
// can recover on unsold stock.
export const SALVAGE_OPTIONS: { value: SalvageTier; label: string }[] = [
  { value: "cost", label: "Close to cost (~90%)" },
  { value: "half", label: "50% off" },
  { value: "clearance", label: "Nothing" },
];

/** Resolve a salvage tier to a per-unit value from the current price and cost. */
export function salvageValueFor(
  tier: SalvageTier,
  price: number,
  cost: number
): number {
  switch (tier) {
    case "clearance":
      return 0;
    case "half":
      return price * 0.5;
    case "cost":
      // A liquidation haircut, not full cost: recovering 90% keeps a small
      // overage cost so "close to cost" still surfaces a leftover loss rather
      // than collapsing to the (degenerate) overstock-free case.
      return cost * 0.9;
  }
}

/**
 * Neutral, round placeholder values the user is meant to change. Deliberately
 * not derived from any Wayflyer merchant data. 1,000 units at a 50/30 price/cost
 * is a 40% margin: clearance salvage gives a 0.4 critical ratio (order a little
 * below 1,000), and — crucially — a sub-50% margin means the "50% off" and "close
 * to cost" salvage tiers both recover *less* than cost, so every salvage option
 * surfaces the missed-margin / leftover-loss trade-off the tool is built to show.
 * (A margin above 50% would make half-price liquidation profitable, collapsing
 * those tiers to the no-downside overstock-free case.)
 */
export const DEFAULTS = {
  demand: "1000",
  spread: "tight" as DemandSpread,
  price: "50",
  cost: "30",
  salvage: "clearance" as SalvageTier,
} as const;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Order quantity calculator FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "How does this calculator decide how much stock to order?",
    answer:
      "It weighs two costs against each other: the margin you'd miss on a sale you can't fulfil if you run out, against the loss on any unit left unsold. The quantity it suggests is the point where ordering one more unit is exactly as likely to be wasted as it is to save a lost sale, given how confident you are in your demand estimate.",
  },
  {
    question: "What is the newsvendor model?",
    answer:
      "The newsvendor model is a classic inventory formula for one-shot ordering decisions, buying stock once for a fixed selling window with no reorders, which is exactly the shape of a Black Friday order. It balances the cost of ordering too little (a stockout) against the cost of ordering too much (unsold leftovers) to find the quantity that maximises expected profit. This calculator is a plain-language version of that model.",
  },
  {
    question: "What is stockout cost, and how is it factored in?",
    answer:
      "Stockout cost is the profit you miss on every sale you can't fulfil because you ran out of stock, which this tool takes as your margin per unit. A higher margin makes running out more costly relative to overordering, so the calculator recommends carrying more stock as a buffer.",
  },
  {
    question: 'What does "How sure are you about that?" actually change?',
    answer:
      "It sets how wide a range of outcomes the calculator plans around. **Fairly sure** narrows the spread of likely demand, so the recommended order sits close to your estimate; **Could be off a lot** widens it, which usually pushes the order quantity further from your estimate to protect against a bigger miss in either direction.",
  },
  {
    question: 'What does "Leftover units are worth" change?',
    answer:
      "It sets how much you can recover on unsold stock, from **Close to cost** at one end down to **Nothing** if it's a genuine write-off. The less you can recover, the more expensive overordering becomes relative to running out, so the calculator recommends a smaller order.",
  },
  {
    question: "What if I can only order in bulk multiples, like cases of 200?",
    answer:
      "Enter that multiple in **Order in multiples of**, and the recommendation is rounded to the nearest quantity you can actually order, rather than an exact number your supplier can't fulfil.",
  },
  {
    question:
      "Where can I get help with the rest of my Black Friday stock planning?",
    answer:
      "Map your order deadline back from sale day with the [backwards planner](/tools/backwards-planner), or check whether financing the extra stock pays for itself with the [financing break-even calculator](/tools/financing-break-even). Browse every tool in the [full Black Friday toolkit](/tools).",
  },
];
